import { facilityNameKey } from '@dcp/shared'

/** The facility form fields the database keeps unique (ADR 0021 + `sage_location_id UNIQUE`). */
export type FacilityUniqueField = 'name' | 'legal_name' | 'sage_location_id'

/**
 * Why a facility write was refused with `23505`, for the form to show.
 * `field` is null when no current facility explains the conflict (it was
 * renamed or deleted between the failed write and the lookup).
 */
export interface FacilityDuplicate {
  field: FacilityUniqueField | null
  message: string
}

/** The columns of another facility the conflict can be found in. */
export interface FacilityUniqueValues {
  id: string
  name: string
  legal_name: string | null
  sage_location_id: string | null
}

/** The unique values the form tried to save; an absent key was not sent. */
export interface SubmittedFacilityValues {
  name?: string
  legal_name?: string
  sage_location_id?: string
}

/** Shown when no current facility explains the `23505`. */
export const FACILITY_DUPLICATE_FALLBACK = 'This name is already used by another facility.'

const SAGE_ID_DUPLICATE = 'Sage Facility ID already exists. Please use a different ID.'

/**
 * Explain a `23505` from a facility insert/update by finding the facility that
 * already holds one of the submitted values.
 *
 * The database reports the violated index or trigger, but index names differ
 * between tenants (ADR 0021), so the answer is recomputed from the current
 * rows instead of read from the error. Names compare with
 * {@link facilityNameKey}, the twin of the `lower(btrim(...))` indexes; the
 * Sage id compares exactly, like its plain `UNIQUE` constraint.
 *
 * @param submitted - Values sent in the failed write.
 * @param facilities - Current facilities of the organization.
 * @param editedId - The facility being edited (its own names never conflict), or null on create.
 */
export function explainFacilityDuplicate(
  submitted: SubmittedFacilityValues,
  facilities: FacilityUniqueValues[],
  editedId: string | null,
): FacilityDuplicate {
  const others = facilities.filter((f) => f.id !== editedId)
  const sameName = (value: string | null, key: string) =>
    value !== null && facilityNameKey(value) === key

  if (submitted.name !== undefined) {
    const display = submitted.name.trim()
    const key = facilityNameKey(display)
    const byDisplay = others.find((f) => sameName(f.name, key))
    if (byDisplay) {
      return {
        field: 'name',
        message: `Display Name "${display}" is already used by facility "${byDisplay.name}".`,
      }
    }
    const byLegal = others.find((f) => sameName(f.legal_name, key))
    if (byLegal) {
      return {
        field: 'name',
        message: `Display Name "${display}" is already the Legal Name of facility "${byLegal.name}".`,
      }
    }
  }

  if (submitted.legal_name !== undefined) {
    const legal = submitted.legal_name.trim()
    const key = facilityNameKey(legal)
    const byLegal = others.find((f) => sameName(f.legal_name, key))
    if (byLegal) {
      return {
        field: 'legal_name',
        message: `Facility Legal Name "${legal}" is already used by facility "${byLegal.name}".`,
      }
    }
    const byDisplay = others.find((f) => sameName(f.name, key))
    if (byDisplay) {
      return {
        field: 'legal_name',
        message: `Facility Legal Name "${legal}" is already the Display Name of facility "${byDisplay.name}".`,
      }
    }
  }

  if (submitted.sage_location_id !== undefined) {
    const sageId = submitted.sage_location_id.trim()
    if (sageId !== '' && others.some((f) => f.sage_location_id === sageId)) {
      return { field: 'sage_location_id', message: SAGE_ID_DUPLICATE }
    }
  }

  return { field: null, message: FACILITY_DUPLICATE_FALLBACK }
}
